import { AfterViewInit, Directive, ElementRef, inject, OnDestroy, output, Renderer2 } from '@angular/core';
import { xIcon } from '@progress/kendo-svg-icons';
/**
 * This directive is to be applied to `<kendo-dropdownlist>` elements.
 * It dynamically injects a clear-selection button.
 *
 * The `(cleared)` event should be captured in the component, and used to reset the dropdown state.
 */
@Directive({
  selector: '[unityClearableDropdownlist]',
  standalone: true,
})
export class ClearableDropdownlistDirective implements AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private mutationObserver?: MutationObserver;

  private svgMarkup = xIcon.content;

  // Stolen from other Kendo elements
  private kTemplate = `
  <kendo-icon-wrapper name="${xIcon.name}" class="k-icon-wrapper-host">
    <kendo-svgicon class="k-svg-i-${xIcon.name} k-svg-icon k-icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" style="pointer-events: none;">
      ${this.svgMarkup}
      </svg>
    </kendo-svgicon>
  </kendo-icon-wrapper>
`;

  private onClick = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    this.clearedEvent.emit();
  };

  private _clearButton?: HTMLElement;
  private get clearButton() {
    if (!this._clearButton) {
      const clearBtn = this.renderer.createElement('span');
      this.renderer.setAttribute(clearBtn, 'role', 'button');
      this.renderer.addClass(clearBtn, 'k-clear-value');
      this.renderer.addClass(clearBtn, 'dropdown-clear-x'); // See: general-form.scss

      clearBtn.innerHTML = this.kTemplate;

      this.renderer.listen(clearBtn, 'click', this.onClick);
      this._clearButton = clearBtn;
    }
    return this._clearButton;
  }

  private setDisabled(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.setStyle(this.clearButton, 'display', 'none');
    } else {
      this.renderer.removeStyle(this.clearButton, 'display');
    }
  }

  private onMutation(mutations: MutationRecord[]): void {
    mutations.forEach(mutation => {
      if (mutation.attributeName === 'class') {
        const hostDisabled = (mutation.target as HTMLElement).classList.contains('k-disabled');
        this.setDisabled(hostDisabled);
      }
    });
  }

  // Event
  public clearedEvent = output();

  public ngAfterViewInit(): void {
    const hostElem: HTMLElement = this.el.nativeElement;

    const inputInner = hostElem.querySelector('.k-input-inner');
    const dropdownArrow = hostElem.querySelector('.k-input-button');

    if (inputInner && dropdownArrow) {
      this.renderer.insertBefore(hostElem, this.clearButton, dropdownArrow);
    } else {
      console.error('ClearableDropdownlistDirective: could not find binding elements. Please check directive useage.');
    }

    // Disabled the injected element along with the parent
    this.mutationObserver = new MutationObserver(this.onMutation.bind(this));
    this.mutationObserver.observe(hostElem, { attributes: true, attributeFilter: ['class'] });

    // Set initial disabled state
    this.setDisabled(hostElem.classList.contains('k-disabled'));
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }
}
